import { useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Check, SkipForward,
  Sparkles, User, Download, CheckCircle2, Network,
  Search, Monitor, Link2, Shield, PartyPopper
} from 'lucide-react';
import useSiteSetupStore from '../../stores/siteSetupStore';
import useAuthStore from '../../stores/authStore';
import WelcomeStep from './steps/WelcomeStep';
import ProfileStep from './steps/ProfileStep';
import AgentSetupStep from './steps/AgentSetupStep';
import AgentVerifyStep from './steps/AgentVerifyStep';
import NetworkConfigStep from './steps/NetworkConfigStep';
import DiscoveryScanStep from './steps/DiscoveryScanStep';
import MonitoringStep from './steps/MonitoringStep';
import IntegrationSelectStep from './steps/IntegrationSelectStep';
import IntegrationConfigStep from './steps/IntegrationConfigStep';
import RecordingAssuranceStep from './steps/RecordingAssuranceStep';
import CompletionStep from './steps/CompletionStep';

const STEP_ICONS = {
  welcome: Sparkles,
  profile: User,
  agent_setup: Download,
  agent_verify: CheckCircle2,
  network_config: Network,
  discovery_scan: Search,
  monitoring: Monitor,
  integrations: Link2,
  integration_config: Link2,
  recording_assurance: Shield,
  completion: PartyPopper,
};

const STEP_COMPONENTS = {
  welcome: WelcomeStep,
  profile: ProfileStep,
  agent_setup: AgentSetupStep,
  agent_verify: AgentVerifyStep,
  network_config: NetworkConfigStep,
  discovery_scan: DiscoveryScanStep,
  monitoring: MonitoringStep,
  integrations: IntegrationSelectStep,
  integration_config: IntegrationConfigStep,
  recording_assurance: RecordingAssuranceStep,
  completion: CompletionStep,
};

const SiteSetupWizard = () => {
  const {
    isWizardOpen,
    loading,
    steps,
    currentStepIndex,
    completedSteps,
    goToNextStep,
    goToPrevStep,
    skipWizard,
    loadStatus,
    getCurrentStep,
    isStepComplete,
    closeWizard,
  } = useSiteSetupStore();

  const { currentTenant, isAdmin } = useAuthStore();

  // Load onboarding status when tenant changes
  useEffect(() => {
    if (currentTenant?.id && isAdmin()) {
      loadStatus();
    }
  }, [currentTenant?.id]);

  // Don't render anything if not open or still loading
  if (!isWizardOpen || loading) return null;

  const currentStep = getCurrentStep();
  const StepComponent = STEP_COMPONENTS[currentStep.key];

  // All phases available
  const availableSteps = steps;
  const currentPhaseIndex = availableSteps.findIndex(s => s.key === currentStep.key);
  const isFirstStep = currentStepIndex === 0;
  const isCompletionStep = currentStep.key === 'completion';

  const handleSkip = async () => {
    await skipWizard(7);
  };

  const handleNext = () => {
    goToNextStep();
  };

  const handleBack = () => {
    goToPrevStep();
  };

  return (
    <div className="site-setup-overlay">
      <div className="site-setup-modal">
        {/* Header */}
        <div className="site-setup-header">
          <div className="site-setup-header-left">
            <Sparkles size={20} />
            <h2>Site Setup</h2>
            {currentTenant?.name && (
              <span className="site-setup-tenant-badge">{currentTenant.name}</span>
            )}
          </div>
          <button className="site-setup-close" onClick={() => closeWizard()} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        <div className="site-setup-stepper">
          {availableSteps.map((step, idx) => {
            const Icon = STEP_ICONS[step.key] || Sparkles;
            const globalIdx = steps.findIndex(s => s.key === step.key);
            const isActive = globalIdx === currentStepIndex;
            const isDone = isStepComplete(step.key);
            const isPast = globalIdx < currentStepIndex;

            return (
              <div
                key={step.key}
                className={
                  'site-setup-stepper-item' +
                  (isActive ? ' active' : '') +
                  (isDone || isPast ? ' completed' : '')
                }
              >
                <div className="site-setup-stepper-dot">
                  {isDone || isPast ? <Check size={12} /> : <Icon size={12} />}
                </div>
                <span className="site-setup-stepper-label">{step.label}</span>
                {idx < availableSteps.length - 1 && <div className="site-setup-stepper-line" />}
              </div>
            );
          })}
        </div>

        {/* Body */}
        <div className="site-setup-body">
          {StepComponent ? (
            <StepComponent
              onNext={handleNext}
              onBack={handleBack}
              onSkip={handleSkip}
            />
          ) : (
            <div className="site-setup-placeholder">
              <p>This step is coming in a future update.</p>
              <button className="site-setup-btn primary" onClick={handleNext}>
                Skip for Now <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="site-setup-footer">
          <div className="site-setup-footer-left">
            {!isFirstStep && !isCompletionStep && (
              <button className="site-setup-btn secondary" onClick={handleBack}>
                <ChevronLeft size={16} /> Back
              </button>
            )}
          </div>
          <div className="site-setup-footer-center">
            {!isCompletionStep && (
              <button className="site-setup-btn ghost" onClick={handleSkip}>
                <SkipForward size={14} /> Remind Me Later
              </button>
            )}
          </div>
          <div className="site-setup-footer-right">
            {/* Next button is handled by individual step components for validation */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteSetupWizard;
